import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  Tooltip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Collapse,
  CircularProgress,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/DashboardRounded';
import AssignmentIcon from '@mui/icons-material/AssignmentRounded';
import EventAvailableIcon from '@mui/icons-material/EventAvailableRounded';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremiumRounded';
import PersonIcon from '@mui/icons-material/PersonRounded';
import SettingsIcon from '@mui/icons-material/SettingsRounded';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import SchoolIcon from '@mui/icons-material/School';
import FolderIcon from '@mui/icons-material/FolderRounded';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import ExpandLessIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import { motion, AnimatePresence } from 'framer-motion';
import { getStudentUser } from '../api/studentApi';
import StudentSettingsSidebar from './SettingsSidebar';
import { pageTransition } from '../utils/motion';

const drawerWidth = 260;
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const apiUrl = (path) => `${API_BASE_URL}${path}`;

// Known pages keep their existing hardcoded routes. Add aliases here when a
// form from the modules API should open one of these existing pages.
const FORM_ROUTE_MAP = [
  {
    path: '/student/assignments',
    names: ['assignment', 'assignments', 'create assignment', 'view assignment', 'assignment list', 'my assignments'],
    icon: <AssignmentIcon />,
  },
  {
    path: '/student/attendance',
    names: ['attendance', 'student attendance', 'view attendance', 'my attendance'],
    icon: <EventAvailableIcon />,
  },
  {
    path: '/student/certificates',
    names: ['certificate', 'certificates', 'view certificate', 'my certificates'],
    icon: <WorkspacePremiumIcon />,
  },
  {
    path: '/student/profile',
    names: ['profile', 'student profile', 'my profile'],
    icon: <PersonIcon />,
  },
];

const normalizePermissionName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getResponseArray = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.modules)) return data.modules;
  return [];
};

const getModuleId = (module) => Number(module?.module_id ?? module?.id);
const getFormId = (form) => Number(form?.form_id ?? form?.id);
const getModuleName = (module) => module?.module_name || module?.name || module?.title || `Module ${getModuleId(module)}`;
const getFormName = (form) => form?.form_name || form?.name || form?.title || `Form ${getFormId(form)}`;

const getFormRoute = (formName, form) => {
  const normalized = normalizePermissionName(formName);
  const mapped = FORM_ROUTE_MAP.find((entry) => entry.names.includes(normalized));
  if (mapped) return mapped.path;
  // Support an explicit route if the modules API supplies one.
  const apiRoute = form?.route || form?.path || form?.url;
  if (typeof apiRoute === 'string' && apiRoute.startsWith('/')) return apiRoute;
  return null;
};

const getFormIcon = (formName) => {
  const normalized = normalizePermissionName(formName);
  const mapped = FORM_ROUTE_MAP.find((entry) => entry.names.includes(normalized));
  return mapped?.icon || <DescriptionIcon />;
};

const StudentLayout = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [moduleGroups, setModuleGroups] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});

  const user = getStudentUser();
  const displayName = user?.admission_details?.student_name || user?.employee_name || 'Student';
  const roleId = user?.role_id ?? user?.admission_details?.role_id;

  useEffect(() => {
    let cancelled = false;

    const fetchStudentPermissions = async () => {
      setPermissionsLoading(true);
      setPermissionsError(false);

      try {
        const token =
          localStorage.getItem('accessToken') ||
          localStorage.getItem('access_token') ||
          localStorage.getItem('token');

        if (roleId == null || roleId === '' || !token || !API_BASE_URL) {
          throw new Error('Student role, access token, or API base URL is missing.');
        }

        const headers = { Authorization: `Bearer ${token}` };
        const [permissionsResponse, modulesResponse] = await Promise.all([
          axios.get(apiUrl(`/api/elearning_settings/module-form-permissions/?role_id=${roleId}`), { headers }),
          axios.get(apiUrl('/api/elearning_settings/client/modules/'), { headers }),
        ]);

        const roleRows = getResponseArray(permissionsResponse);
        const modules = getResponseArray(modulesResponse);
        const roleData = roleRows.find((item) => Number(item?.role_id) === Number(roleId));

        if (!roleData || !Array.isArray(roleData.permissions) || modules.length === 0) {
          throw new Error('Permission response is missing or has an unexpected format.');
        }

        const permissionLookup = new Map();
        roleData.permissions.forEach((permission) => {
          const key = `${Number(permission.module_id)}:${Number(permission.form_id)}`;
          permissionLookup.set(key, permission);
        });

        const allowedGroups = modules
          .map((module) => {
            const moduleId = getModuleId(module);
            const forms = Array.isArray(module?.forms) ? module.forms : [];
            const readableForms = forms
              .map((form) => {
                const formId = getFormId(form);
                const permission = permissionLookup.get(`${moduleId}:${formId}`);
                if (permission?.can_read !== true) return null;
                const formName = getFormName(form);
                return {
                  id: `${moduleId}:${formId}`,
                  moduleId,
                  formId,
                  name: formName,
                  route: getFormRoute(formName, form),
                  icon: getFormIcon(formName),
                  canCreate: permission?.can_create === true,
                  canUpdate: permission?.can_update === true,
                  canDelete: permission?.can_delete === true,
                };
              })
              .filter(Boolean);

            return {
              id: moduleId,
              name: getModuleName(module),
              forms: readableForms,
            };
          })
          .filter((module) => module.forms.length > 0);

        if (!cancelled) {
          setModuleGroups(allowedGroups);
          // Expand the module containing the current route by default; otherwise
          // expand the first permitted module so its forms are immediately visible.
          const currentModule = allowedGroups.find((module) =>
            module.forms.some((form) => form.route && location.pathname.startsWith(form.route)),
          );
          const initialModule = currentModule || allowedGroups[0];
          setExpandedModules(initialModule ? { [initialModule.id]: true } : {});
        }
      } catch (error) {
        console.error('Failed to load Student Panel permissions:', error);
        if (!cancelled) {
          setPermissionsError(true);
          setModuleGroups([]);
          setExpandedModules({});
        }
      } finally {
        if (!cancelled) setPermissionsLoading(false);
      }
    };

    fetchStudentPermissions();
    return () => {
      cancelled = true;
    };
  }, [roleId]);

  const activeForm = useMemo(() => {
    for (const module of moduleGroups) {
      const form = module.forms.find((item) => item.route && location.pathname.startsWith(item.route));
      if (form) return { module, form };
    }
    return null;
  }, [moduleGroups, location.pathname]);

  useEffect(() => {
    if (permissionsLoading || permissionsError || !location.pathname.startsWith('/student/')) return;
    if (location.pathname === '/student/dashboard') return;

    const isAllowed = moduleGroups.some((module) =>
      module.forms.some((form) => form.route && (
        location.pathname === form.route || location.pathname.startsWith(`${form.route}/`)
      )),
    );

    if (!isAllowed) {
      const firstRoute = moduleGroups.flatMap((module) => module.forms).find((form) => form.route)?.route;
      navigate(firstRoute || '/student/dashboard', { replace: true });
    }
  }, [location.pathname, moduleGroups, navigate, permissionsError, permissionsLoading]);

  const handleLogoutClick = () => setLogoutConfirmOpen(true);

  const handleLogoutConfirm = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('client');
    sessionStorage.removeItem('user');
    navigate('/');
  };

  const handleNav = (path) => {
    if (!path) return;
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((previous) => ({ ...previous, [moduleId]: !previous[moduleId] }));
  };

  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${isDark ? '#15152A' : '#2A2A6E'} 130%)`,
        color: '#fff',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.75, py: 3 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha('#fff', 0.16),
            backdropFilter: 'blur(6px)',
          }}
        >
          <SchoolIcon fontSize="small" />
        </Box>
        <Typography sx={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05rem' }}>
          Student Panel
        </Typography>
      </Box>

      <List sx={{ px: 1.5, flexGrow: 1, overflowY: 'auto' }}>
        <ListItemButton
          selected={location.pathname === '/student/dashboard'}
          onClick={() => handleNav('/student/dashboard')}
          sx={{
            position: 'relative',
            mb: 0.5,
            borderRadius: '12px',
            color: location.pathname === '/student/dashboard' ? '#fff' : alpha('#fff', 0.72),
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: alpha('#fff', 0.16), color: '#fff' },
            '&:hover': { backgroundColor: alpha('#fff', 0.08) },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}><DashboardIcon /></ListItemIcon>
          <ListItemText primary="Dashboard" primaryTypographyProps={{ fontWeight: location.pathname === '/student/dashboard' ? 700 : 500, fontSize: '0.92rem' }} />
        </ListItemButton>

        {permissionsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} sx={{ color: '#fff' }} />
          </Box>
        ) : permissionsError ? (
          <Typography sx={{ px: 1.5, py: 2, color: alpha('#fff', 0.75), fontSize: '0.85rem' }}>
            Unable to load module permissions.
          </Typography>
        ) : moduleGroups.length > 0 ? (
          moduleGroups.map((module) => {
            const expanded = Boolean(expandedModules[module.id]);
            const moduleActive = module.forms.some((form) => form.route && location.pathname.startsWith(form.route));
            return (
              <Box key={module.id} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => toggleModule(module.id)}
                  aria-expanded={expanded}
                  sx={{
                    borderRadius: '12px',
                    color: moduleActive ? '#fff' : alpha('#fff', 0.82),
                    backgroundColor: moduleActive ? alpha('#fff', 0.08) : 'transparent',
                    '&:hover': { backgroundColor: alpha('#fff', 0.08) },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}><FolderIcon /></ListItemIcon>
                  <ListItemText primary={module.name} primaryTypographyProps={{ fontWeight: moduleActive ? 700 : 500, fontSize: '0.92rem' }} />
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {module.forms.map((form) => {
                      const active = Boolean(form.route && location.pathname.startsWith(form.route));
                      return (
                        <ListItemButton
                          key={form.id}
                          disabled={!form.route}
                          onClick={() => handleNav(form.route)}
                          title={!form.route ? 'This form needs a route mapping in FORM_ROUTE_MAP.' : form.name}
                          sx={{
                            pl: 5.5,
                            pr: 1.5,
                            py: 0.8,
                            mb: 0.25,
                            borderRadius: '10px',
                            color: active ? '#fff' : alpha('#fff', 0.72),
                            '&.Mui-disabled': { color: alpha('#fff', 0.42) },
                            '&:hover': { backgroundColor: alpha('#fff', 0.08) },
                          }}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 30 }}>{form.icon}</ListItemIcon>
                          <ListItemText
                            primary={form.name}
                            primaryTypographyProps={{ fontSize: '0.84rem', fontWeight: active ? 700 : 400 }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })
        ) : (
          <Typography sx={{ px: 1.5, py: 2, color: alpha('#fff', 0.75), fontSize: '0.85rem' }}>
            No permitted forms available for your role.
          </Typography>
        )}
      </List>

      <Box sx={{ px: 2.5, py: 2.5, borderTop: `1px solid ${alpha('#fff', 0.12)}` }}>
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.55) }}>Signed in as</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{displayName}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: alpha(theme.palette.background.paper, 0.72),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {isMobile ? (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: 'inherit' }}><DashboardIcon /></IconButton>
          ) : (
            <Typography sx={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
              {activeForm?.form.name || (location.pathname === '/student/dashboard' ? 'Dashboard' : 'Student Panel')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600 }}>{displayName}</Typography>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontWeight: 700,
                fontSize: '0.9rem',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Tooltip title="Settings"><IconButton onClick={() => setSettingsOpen(true)} sx={{ color: 'inherit' }}><SettingsIcon /></IconButton></Tooltip>
            <Tooltip title="Log out"><IconButton onClick={handleLogoutClick} sx={{ color: 'inherit' }}><LogoutIcon /></IconButton></Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', border: 'none' } }}
        >
          {sidebarContent}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, width: { md: `calc(100% - ${drawerWidth}px)` }, backgroundColor: 'background.default' }}>
        <Toolbar />
        <AnimatePresence mode="wait">
          <motion.div key={location.pathname} variants={pageTransition} initial="initial" animate="animate" exit="exit">
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </Box>

      <StudentSettingsSidebar open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <Dialog open={logoutConfirmOpen} onClose={() => setLogoutConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Log out?</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to log out of your account?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutConfirmOpen(false)} data-app-cancel="true">Cancel</Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained">Log out</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentLayout;
